import { Alert, Badge, Button, Col, Collapse, Divider, Flex, Popconfirm, Row, Space, Typography, theme } from 'antd';
import { FC, ReactNode } from 'react';
import {
  BoxArrowUpRight,
  ChevronLeft,
  ExclamationCircle,
  PencilSquare,
  Trash,
  XCircleFill
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

import { CopyableText, DescriptionBlock, DomainPunycode, OpenInNewTabIcon } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { CommonDrawerTitle } from '@/common/layouts';
import { getDateFactory } from '@/common/utils';

import { Domain, DomainDeployStatus, DomainDeployStatusTag, DomainHealthTag, DomainWWWRedirect } from '@/modules/Domains';
import { DomainCheckHealthWithTag } from '@/modules/Domains/components/DomainCheckHealthWithTag/DomainCheckHealthWithTag';


interface Props {
  domain: Domain;
  isDeleteDomainLoading: boolean;
  isDisabledControlButtons: boolean;
  handleDeleteDomain: () => void;
}

export const DomainsOverviewView: FC<Props> = ({
  domain,
  isDeleteDomainLoading,
  isDisabledControlButtons,
  handleDeleteDomain,
}) => {
  const { token } = theme.useToken();

  return (
    <>
      <CommonDrawerTitle
        onlyExtra
        extra={
          <Space size="small">
            { /* <Dropdown
                menu={{
                  onClick: handleMenuClick,
                  items: [
                    {
                      label: 'Редагувати',
                      key: '0',
                      icon: <PencilSquare/>,
                      onClick: handleEditDomain
                    },
                    {
                      label: (
                        <Popconfirm
                          disabled={ isDisabledControlButtons }
                          title="Видалити цей домен?"
                          onConfirm={ handleDeleteDomain }
                          okType="danger"
                          okText="Так"
                          icon={ <ExclamationCircle color={token.colorError}/> }
                          placement="bottomRight"
                        >
                          Видалити
                        </Popconfirm>
                      ),
                      danger: true,
                      key: '1',
                      icon: <Trash/>
                      // onClick: handleEditDomain,
                    }
                  ]
                }}
              >
                <Button disabled={ isDisabledControlButtons } size="small" shape="circle" icon={ <ThreeDots/> }/>
              </Dropdown> */ }

            <Button
              type="dashed"
              shape="round"
              icon={ <OpenInNewTabIcon size={ 18 }/> }
              title="Відкрити в новій вкладці"
              target="_blank"
              disabled={ !domain.isDeployed }
              href={
                `${ domain.isHttps ? 'https' : 'http' }://${
                  domain.wwwRedirect === DomainWWWRedirect.WithoutWWWToWWW ? 'www.' : ''
                }${ domain.name }`
              }
            />

            <Divider type="vertical"/>

            <Link to={ AppRoutes.DomainsEdit.replace(':domainId', domain.id) }>
              <Button disabled={ isDisabledControlButtons } icon={ <PencilSquare/> }>
                Редагувати
              </Button>
            </Link>

            <Popconfirm
              disabled={ isDisabledControlButtons }
              title="Видалити цей домен?"
              onConfirm={ handleDeleteDomain }
              okType="danger"
              okText="Так"
              icon={ <ExclamationCircle color={ token.colorError }/> }
              placement="bottomRight"
            >
              <Button loading={ isDeleteDomainLoading } disabled={ isDisabledControlButtons } danger icon={ <Trash/> }/>
            </Popconfirm>
          </Space>
        }
      />

      <Row gutter={ [16, 16] }>
        { domain.deployStatus === DomainDeployStatus.Failed && (
          <Col span={ 24 }>
            <Alert
              showIcon
              icon={ <XCircleFill size={ 18 }/> }
              type="error"
              message={
                <Collapse
                  ghost
                  expandIconPosition="end"
                  className="proxy-server__addresses-list"
                  accordion
                  expandIcon={ (properties): ReactNode => (
                    <Badge dot={ properties.className?.includes('haveError') }>
                      <ChevronLeft size={ 16 } style={{ transition: '300ms', rotate: `${ properties.isActive ? -90 : 0 }deg` }}/>
                    </Badge>
                  ) }
                >
                  <Collapse.Panel
                    key="deploy-error-info"
                    collapsible={ domain.deployErrorDescription ? 'icon' : 'disabled' }
                    style={{
                      gap: 10,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    header={
                      <Typography.Paragraph
                        style={{ marginBottom: 0 }}
                        ellipsis={{
                          rows: 3,
                          expandable: true
                        }}
                        type="danger"
                      >
                        { domain.deployErrorMessage }
                      </Typography.Paragraph>
                    }
                  >
                    <Typography.Text type="danger" italic style={{ whiteSpace: 'pre-wrap' }}>
                      Додаткова інформація:
                      <br/>
                      { domain.deployErrorDescription }
                    </Typography.Text>
                  </Collapse.Panel>
                </Collapse>
              }
            />
            <Divider style={{ marginBottom: 0, marginTop: 18 }}/>
          </Col>
        ) }

        <Col span={ 24 }>
          <Flex style={{ width: '100%' }} justify="space-between" align="middle">
            <DomainDeployStatusTag status={ domain.deployStatus }/>
            {
              domain.deployStatus === DomainDeployStatus.Deployed &&
              <DomainCheckHealthWithTag domainId={ domain.id } tag={ <DomainHealthTag status={ domain.healthStatus }/> }/>
            }
          </Flex>
        </Col>

        <Col span={ 24 }>
          <DescriptionBlock
            items={ [[
              'Назва',
              <CopyableText key="domain.name" textToCopy={ DomainPunycode.stringFormat(domain.name) }>
                <DomainPunycode domainName={ domain.name }/>
              </CopyableText>
            ]] }/>

          <DescriptionBlock items={ [['SSL-сертифікатів згенеровано за останні 7 днів', domain.sslGenerationsCount]] }/>

          <DescriptionBlock
            items={ [
              [
                'Реєстратор',
                <a
                  key="Реєстратор"
                  href={ AppRoutes.DomainRegistrarsById.replace(':domainRegistrarId', domain.domainRegistrar.id) }
                  target="_blank"
                  rel="noreferrer"
                >
                  { domain.domainRegistrar?.name }&nbsp;
                  <BoxArrowUpRight/>
                </a>
              ]
            ] }
          />

          <DescriptionBlock
            items={ [
              [
                'Акаунт реєстратора',
                <div key="accountLogin">
                  <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
                    Логін:&nbsp;
                  </Typography.Text>
                  { domain.domainRegistrarAccount?.login }
                </div>
              ]
            ] }
          />

          <DescriptionBlock items={ [['Дата реєстрації', getDateFactory(domain.registratedAt, 'August 16, 2018 08:02', true)]] }/>

          <DescriptionBlock items={ [['Дата оплати', getDateFactory(domain.paidAt, 'August 16, 2018 08:02', true)]] }/>
        </Col>
      </Row>
    </>
  );
};
